import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, Eye, MessageSquare, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessagingAdminPage = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchConversations();
  }, [skip]);

  const fetchConversations = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip, limit });
      if (search) params.append('search', search);

      const response = await axios.get(`${API}/admin/messages?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConversations(response.data.conversations);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load conversations');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSkip(0);
    fetchConversations();
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Messaging Overview</h1>
        <p className="text-gray-600 mt-2">View all conversations between companies</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1 flex items-center space-x-2">
              <Input
                placeholder="Search by company name, thread ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} style={{ backgroundColor: '#62D84E' }}>
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>

            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Conversations Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#62D84E' }}></div>
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No conversations found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thread ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">From Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">To Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Messages</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Message</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {conversations.map((conversation) => (
                    <tr key={conversation.thread_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-mono text-gray-600">
                        {conversation.thread_id ? conversation.thread_id.substring(0, 8) : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <MessageSquare className="w-5 h-5 mr-2 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">
                            {conversation.participants && conversation.participants.length > 0 
                              ? conversation.participants[0]?.name 
                              : 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {conversation.participants && conversation.participants.length > 1
                          ? conversation.participants[1]?.name
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{conversation.subject || 'No subject'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{conversation.message_count || 0}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {conversation.latest_message?.created_at 
                          ? new Date(conversation.latest_message.created_at).toLocaleDateString() 
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {conversation.created_at ? new Date(conversation.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.location.href = `/admin/messages/${conversation.thread_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {skip + 1} to {Math.min(skip + limit, total)} of {total} conversations
        </p>
        <div className="space-x-2">
          <Button
            variant="outline"
            onClick={() => setSkip(Math.max(0, skip - limit))}
            disabled={skip === 0}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setSkip(skip + limit)}
            disabled={skip + limit >= total}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MessagingAdminPage;
